import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import HttpManager from "../../../services/HttpManager";
import { ActividadNueva } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { errorTexto, localMxAIso } from "./salesHelpers";
import { festejo } from "./festejo";
import "./formCita.css";

/**
 * Cita de Matrícula en tres pasos, con botones grandes (Esteban, 2026-10-13: "sin fecha no hay Promesa", y que se sienta
 * como juego): ¿Cómo? (plantel / digital) · ¿Qué día? · ¿Te dio hora? (hora exacta, franja o solo el día).
 * Nunca se pone una hora sola: sin hora va solo la fecha y, si la dio, la franja. En Monterrey (plantel 1) se dice
 * "Cita de Inscripción". El mismo componente sirve para un Seguimiento (sin el paso 1, tipo "llamar").
 */

export type Franja = "manana" | "tarde" | "noche";
export type HoraElegida = { modo: "exacta" | Franja | "dia" | null; hora: string };

const MX_MS = 6 * 3600000;
/** Fecha local (UTC-6) de hoy + n días, "YYYY-MM-DD". */
export const diaLocal = (n = 0): string => new Date(Date.now() - MX_MS + n * 86400000).toISOString().slice(0, 10);
const DIAS_CORTOS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const partes = (f: string) => { const [y, m, d] = f.split("-").map(Number); return { y, m, d, dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay() }; };
/** "jue 16" */
export const diaCorto = (f: string): string => { const p = partes(f); return `${DIAS_CORTOS[p.dow]} ${p.d}`; };
/** "hoy", "mañana", "jue 16 Oct" */
export const diaTexto = (f: string): string => f === diaLocal(0) ? "hoy" : f === diaLocal(1) ? "mañana" : (() => { const p = partes(f); return `${DIAS_CORTOS[p.dow]} ${p.d} ${MESES[p.m - 1]}`; })();
/** "15:00" → "3:00 pm" */
export const hora12 = (hhmm: string): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || "");
	if (!m) return hhmm;
	const h = Number(m[1]);
	return `${h % 12 || 12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};
export const FRANJA_LABEL: { [k in Franja]: string } = { manana: "En la mañana", tarde: "En la tarde", noche: "En la noche" };
const HORAS_RAPIDAS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

/** "Cita de Matrícula" o, en Monterrey, "Cita de Inscripción". */
export const tituloCita = (plantelId: number | null | undefined): string => plantelId === 1 ? "Cita de Inscripción" : "Cita de Matrícula";

/** Resumen corto de una actividad nueva: "jue 16 · 3:00 pm · en el plantel". */
export const resumenActividad = (a: ActividadNueva): string => {
	const dia = a.fecha ? diaTexto(a.fecha) : a.at ? diaTexto(new Date(new Date(a.at).getTime() - MX_MS).toISOString().slice(0, 10)) : "";
	const h = a.at && a.hora_exacta ? hora12(new Date(new Date(a.at).getTime() - MX_MS).toISOString().slice(11, 16)) : a.franja ? FRANJA_LABEL[a.franja].toLowerCase() : "sin hora";
	const mod = a.modalidad === "digital" ? "digital" : a.modalidad === "presencial" ? "en el plantel" : null;
	return [dia, h, mod].filter(Boolean).join(" · ");
};

/**
 * Paso 3 suelto (también lo usa el formulario de actividad): hora exacta, franja o solo el día. La hora es OPCIONAL y va
 * escondida tras "Agregar hora" (Esteban, 2026-10-14): vacía = todo el día, vence a las 11:59 pm de ese día.
 */
/** Con `fecha` = hoy, las horas que ya pasaron se apagan (revisión 14-oct: una cita de hoy a las 10 am cuando son las 3 pm). */
export const PasoHora: React.FC<{ value: HoraElegida; onChange: (v: HoraElegida) => void; idBase: string; fecha?: string }> = ({ value, onChange, idBase, fecha }) => {
	const ahora = fecha === diaLocal(0) ? new Date(Date.now() - MX_MS).toISOString().slice(11, 16) : null;
	const pasada = (h: string) => !!ahora && h <= ahora;
	const [abierta, setAbierta] = useState<boolean>(!!value.modo && value.modo !== "dia");
	if (!abierta) {
		return (
			<button type="button" className="fc-mas-hora" onClick={() => setAbierta(true)}>+ Agregar hora <small>(opcional)</small></button>
		);
	}
	const chip = (modo: HoraElegida["modo"], txt: string) => (
		<button type="button" className={"fc-chip" + (value.modo === modo ? " on" : "")} aria-pressed={value.modo === modo}
			onClick={() => onChange({ modo, hora: modo === "exacta" ? value.hora : "" })}>{txt}</button>
	);
	return (
		<div className="fc-hora">
			<div className="fc-chips" role="group" aria-label="¿Te dio hora?">
				{chip("exacta", value.modo === "exacta" && value.hora ? "Sí, a las " + hora12(value.hora) : "Sí, a las…")}
				{chip("manana", "En la mañana")}
				{chip("tarde", "En la tarde")}
				{chip("noche", "En la noche")}
				<button type="button" className="fc-chip" onClick={() => { onChange({ modo: null, hora: "" }); setAbierta(false); }}>Sin hora</button>
			</div>
			{value.modo === "exacta" ? (
				<div className="fc-horas">
					{HORAS_RAPIDAS.map((h) => (
						<button key={h} type="button" className={"fc-h" + (value.hora === h ? " on" : "")} disabled={pasada(h)} title={pasada(h) ? "Ya pasó" : undefined}
							onClick={() => onChange({ modo: "exacta", hora: h })}>{hora12(h).replace(":00", "")}</button>
					))}
					<label className="fc-otra-hora" htmlFor={idBase + "-hora"}>Otra
						<input id={idBase + "-hora"} type="time" value={value.hora} min={ahora || undefined} onChange={(e) => onChange({ modo: "exacta", hora: e.target.value })} /></label>
				</div>
			) : null}
		</div>
	);
};

/** Convierte fecha + hora elegida a lo que pide la API. */
export const armarActividad = (tipo: string, fecha: string, h: HoraElegida, extra: { titulo?: string | null; modalidad?: "presencial" | "digital" | null } = {}): ActividadNueva => {
	const base: ActividadNueva = { tipo, titulo: extra.titulo?.trim() || null };
	if (tipo === "cita") base.modalidad = extra.modalidad || null;
	if (h.modo === "exacta") return { ...base, at: localMxAIso(fecha + "T" + h.hora), hora_exacta: true, franja: null };
	return { ...base, fecha, hora_exacta: false, franja: h.modo === "manana" || h.modo === "tarde" || h.modo === "noche" ? h.modo : null };
};
/** Sin hora (modo null) también vale: es todo el día. Solo "a las…" pide la hora, y que no haya pasado. */
export const horaCompleta = (h: HoraElegida, fecha?: string): boolean => h.modo !== "exacta" || ((/^\d{2}:\d{2}/.test(h.hora)
	&& !(fecha === diaLocal(0) && h.hora <= new Date(Date.now() - MX_MS).toISOString().slice(11, 16))));

interface Props {
	plantelId?: number | null;
	modo: "cita" | "seguimiento";
	/** Promesa: deja escoger entre cita y "solo seguimiento con fecha". */
	permitirSeguimiento?: boolean;
	/** Aviso arriba ("Una Promesa lleva fecha"). */
	aviso?: string | null;
	textoBoton?: string;
	busy?: boolean;
	error?: string | null;
	onListo: (a: ActividadNueva) => void;
	onCancelar: () => void;
}

const FormCita: React.FC<Props> = ({ plantelId, modo: modoInicial, permitirSeguimiento, aviso, textoBoton, busy, error, onListo, onCancelar }) => {
	const [modo, setModo] = useState<"cita" | "seguimiento">(modoInicial);
	const pasos = modo === "cita" ? 3 : 2;
	const [paso, setPaso] = useState<number>(modo === "cita" ? 1 : 2);
	const [modalidad, setModalidad] = useState<"presencial" | "digital" | null>(null);
	const [fecha, setFecha] = useState<string>("");
	const [otroDia, setOtroDia] = useState<boolean>(false);
	const [h, setH] = useState<HoraElegida>({ modo: null, hora: "" });
	const [titulo, setTitulo] = useState<string>("");
	const [falta, setFalta] = useState<string | null>(null);
	useEffect(() => { setPaso(modo === "cita" ? (modalidad ? 2 : 1) : 2); }, [modo]);
	// El aviso de lo que falta se va en cuanto contesta algo.
	useEffect(() => { setFalta(null); }, [modalidad, fecha, h, modo]);

	const titulo1 = modo === "cita" ? tituloCita(plantelId) : "Seguimiento";
	const boton = textoBoton || (modo === "cita" ? (plantelId === 1 ? "Guardar Cita de Inscripción" : "Guardar Cita") : "Guardar Seguimiento");
	const numPaso = modo === "cita" ? paso : paso - 1;
	const listo = () => {
		if (modo === "cita" && !modalidad) { setFalta("¿Es en el plantel o digital?"); setPaso(1); return; }
		if (!fecha) { setFalta("Elige el día."); setPaso(2); return; }
		if (!horaCompleta(h, fecha)) { setFalta(h.hora ? "Esa hora ya pasó." : "Elige la hora, o «Sin hora»."); setPaso(3); return; }
		setFalta(null);
		try { onListo(armarActividad(modo === "cita" ? "cita" : "llamar", fecha, h, { titulo, modalidad })); }
		catch (e) { setFalta(errorTexto(e)); }
	};
	const dias = [0, 1, 2, 3, 4, 5, 6].map((n) => diaLocal(n));
	const elegirDia = (f: string) => { setFecha(f); setOtroDia(false); setPaso(3); };

	return (
		<div className="fc" onKeyDown={(e) => { if (e.key === "Escape") onCancelar(); }}>
			<div className="fc-cab">
				<b className="fc-tit">{titulo1}</b>
				<span className="fc-puntos" aria-label={"Paso " + numPaso + " de " + pasos}>
					{Array.from({ length: pasos }, (_, i) => {
						const n = modo === "cita" ? i + 1 : i + 2;
						return <button key={n} type="button" className={"fc-punto" + (n === paso ? " on" : n < paso ? " hecho" : "")} aria-label={"Ir al paso " + (i + 1)} onClick={() => setPaso(n)} />;
					})}
				</span>
				<button type="button" className="fc-x" onClick={onCancelar} aria-label="Cerrar"><WappIcono n="x" size={18} /></button>
			</div>
			{aviso ? <div className="fc-aviso">{aviso}</div> : null}
			{permitirSeguimiento ? (
				<div className="fc-tipo" role="group" aria-label="Qué agenda">
					<button type="button" className={modo === "cita" ? "on" : ""} aria-pressed={modo === "cita"} onClick={() => setModo("cita")}>{tituloCita(plantelId)}</button>
					<button type="button" className={modo === "seguimiento" ? "on" : ""} aria-pressed={modo === "seguimiento"} onClick={() => setModo("seguimiento")}>Solo seguimiento con fecha</button>
				</div>
			) : null}

			<div className="fc-pasos">
				{modo === "cita" ? (
					<section className={"fc-paso" + (paso === 1 ? " abierto" : "")}>
						<button type="button" className="fc-paso-cab" onClick={() => setPaso(1)}>
							<span className="fc-n">1</span><span>¿Cómo?</span>
							{modalidad && paso !== 1 ? <span className="fc-elegido">{modalidad === "digital" ? "Digital" : "En el plantel"}</span> : null}
						</button>
						{paso === 1 ? (
							<div className="fc-cuerpo fc-tarjetas">
								<button type="button" className={"fc-tarjeta" + (modalidad === "presencial" ? " on" : "")} aria-pressed={modalidad === "presencial"}
									onClick={() => { setModalidad("presencial"); setPaso(2); }}>
									<WappIcono n="building" size={26} /><b>En el plantel</b><small>Viene a la academia</small></button>
								<button type="button" className={"fc-tarjeta" + (modalidad === "digital" ? " on" : "")} aria-pressed={modalidad === "digital"}
									onClick={() => { setModalidad("digital"); setPaso(2); }}>
									<WappIcono n="world" size={26} /><b>Digital</b><small>Se matricula en línea</small></button>
							</div>
						) : null}
					</section>
				) : null}

				<section className={"fc-paso" + (paso === 2 ? " abierto" : "")}>
					<button type="button" className="fc-paso-cab" onClick={() => setPaso(2)}>
						<span className="fc-n">{modo === "cita" ? 2 : 1}</span><span>¿Qué día?</span>
						{fecha && paso !== 2 ? <span className="fc-elegido">{diaTexto(fecha)}</span> : null}
					</button>
					{paso === 2 ? (
						<div className="fc-cuerpo">
							<div className="fc-chips">
								{dias.map((f, i) => (
									<button key={f} type="button" className={"fc-chip" + (fecha === f && !otroDia ? " on" : "")} onClick={() => elegirDia(f)}>
										{i === 0 ? "Hoy" : i === 1 ? "Mañana" : diaCorto(f)}</button>
								))}
								<button type="button" className={"fc-chip" + (otroDia ? " on" : "")} onClick={() => setOtroDia(true)}>Otro día</button>
							</div>
							{otroDia ? <input className="fc-fecha" type="date" min={dias[0]} value={fecha} aria-label="Otro día" autoFocus
								onChange={(e) => { setFecha(e.target.value); if (e.target.value) setPaso(3); }} /> : null}
						</div>
					) : null}
				</section>

				<section className={"fc-paso" + (paso === 3 ? " abierto" : "")}>
					<button type="button" className="fc-paso-cab" onClick={() => setPaso(3)}>
						<span className="fc-n">{modo === "cita" ? 3 : 2}</span><span>Hora y detalles <small>(opcional)</small></span>
						{horaCompleta(h) && paso !== 3 ? <span className="fc-elegido">{h.modo === "exacta" ? (h.hora ? hora12(h.hora) : "Sin hora") : !h.modo || h.modo === "dia" ? "Todo el día" : FRANJA_LABEL[h.modo as Franja]}</span> : null}
					</button>
					{paso === 3 ? (
						<div className="fc-cuerpo">
							<PasoHora value={h} onChange={setH} idBase={"fc-" + modo} fecha={fecha} />
							<label className="fc-para"><span>Para qué <small>(opcional)</small></span>
								<input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={140}
									placeholder={modo === "cita" ? "Trae a su mamá, lleva identificación…" : "Confirmar horario, mandar precio con kit…"}
									onKeyDown={(e) => { if (e.key === "Enter") listo(); }} /></label>
						</div>
					) : null}
				</section>
			</div>

			{falta || error ? <div className="fc-falta" role="alert">{falta || error}</div> : null}
			<div className="fc-pie">
				<button type="button" className="fc-btn" onClick={onCancelar}>Cancelar</button>
				<button type="button" className="fc-btn pri" disabled={busy} onClick={listo}>{busy ? "Guardando…" : boton}</button>
			</div>
		</div>
	);
};

export default FormCita;

/**
 * La cita (o el seguimiento) como ventana encima de la ficha: guarda sola. Con `promesa` pone el status Promesa en la misma
 * llamada (el servidor no deja Promesa sin fecha) y festeja.
 */
export const ModalCita: React.FC<{
	campusId: number; prospectId: number; plantelId?: number | null; modo: "cita" | "seguimiento"; promesa?: boolean; aviso?: string | null;
	/** Deja escoger cita o "solo seguimiento con fecha" (por defecto, solo cuando es Promesa). */
	permitirSeguimiento?: boolean;
	onGuardado: () => void; onCancelar: () => void;
}> = ({ campusId, prospectId, plantelId, modo, promesa, aviso, permitirSeguimiento, onGuardado, onCancelar }) => {
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const guardar = async (a: ActividadNueva) => {
		setBusy(true); setError(null);
		const http = HttpManager.getInstance();
		try {
			if (promesa) {
				await http.prospectsService.status(campusId, prospectId, 4, null, a);
				festejo("promesa");
			} else {
				await http.prospectsService.crearActividad(campusId, prospectId, a);
			}
			onGuardado();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	return createPortal(
		<div className="fc-velo" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancelar(); }}>
			<div className="fc-hoja" role="dialog" aria-modal="true" aria-label={modo === "cita" ? tituloCita(plantelId) : "Seguimiento"}>
				<FormCita plantelId={plantelId} modo={modo} permitirSeguimiento={permitirSeguimiento ?? promesa} aviso={aviso} busy={busy} error={error}
					onListo={guardar} onCancelar={onCancelar} />
			</div>
		</div>,
		document.body,
	);
};
